import React, {
    useMemo,
    useState,
} from "react";

import {
    calculateTariffSelection,
} from "../../../../../domain/tariffs/calculate-tariff-selection";
import {
    baseTariff,
    tariffAddons,
} from "../../content/tariffs";
import LandingArrowIcon from "../../ui/LandingArrowIcon";
import LandingCheckIcon from "../../ui/LandingCheckIcon";

function ChevronIcon({opened}) {
    return (
        <svg
            className={
                opened
                    ? "is-opened"
                    : ""
            }
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
        >
            <path
                d="M4.5 6.75L9 11.25L13.5 6.75"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

export default function TariffConstructor({
    formatPrice,
    t,
}) {
    const [
        selectedAddonIds,
        setSelectedAddonIds,
    ] = useState([]);

    const [
        constructorOpened,
        setConstructorOpened,
    ] = useState(false);

    const tariffSelection = useMemo(
        () =>
            calculateTariffSelection({
                basePrice:
                    baseTariff.price,
                addons:
                    tariffAddons,
                selectedAddonIds,
            }),
        [selectedAddonIds]
    );

    const toggleAddon = (addonId) => {
        setSelectedAddonIds(
            (currentIds) =>
                currentIds.includes(
                    addonId
                )
                    ? currentIds.filter(
                        (id) =>
                            id !== addonId
                    )
                    : [
                        ...currentIds,
                        addonId,
                    ]
        );
    };

    return (
        <>
            <button
                className={`tariff-constructor-toggle${
                    constructorOpened
                        ? " tariff-constructor-toggle--opened"
                        : ""
                }`}
                type="button"
                aria-expanded={
                    constructorOpened
                }
                onClick={() =>
                    setConstructorOpened(
                        (opened) => !opened
                    )
                }
            >
                <span className="tariff-constructor-toggle__content">
                    <span>
                        {t(
                            "main.landing.tariffs.constructor.label"
                        )}
                    </span>

                    <strong>
                        {t(
                            "main.landing.tariffs.constructor.tagline"
                        )}
                    </strong>
                </span>

                <span className="tariff-constructor-toggle__summary">
                    {t(
                        "main.landing.tariffs.constructor.fromPerMonth",
                        {
                            price:
                                formatPrice(
                                    baseTariff.price
                                ),
                        }
                    )}
                </span>

                <span className="tariff-constructor-toggle__action">
                    {constructorOpened
                        ? t(
                            "main.landing.tariffs.constructor.hideSettings"
                        )
                        : t(
                            "main.landing.tariffs.constructor.showSettings"
                        )}

                    <ChevronIcon
                        opened={
                            constructorOpened
                        }
                    />
                </span>
            </button>

            {constructorOpened && (
                <div className="tariff-constructor">
                    <div className="tariff-constructor__heading">
                        <div>
                            <span className="tariff-constructor__eyebrow">
                                {t(
                                    "main.landing.tariffs.constructor.eyebrow"
                                )}
                            </span>

                            <h3 className="tariff-constructor__title">
                                {t(
                                    "main.landing.tariffs.constructor.title"
                                )}
                            </h3>

                            <p className="tariff-constructor__description">
                                {t(
                                    "main.landing.tariffs.constructor.description"
                                )}
                            </p>
                        </div>

                        <div className="tariff-constructor__summary">
                            <div className="tariff-constructor__summary-row">
                                <span>
                                    {t(
                                        "main.landing.tariffs.constructor.basicPlan"
                                    )}
                                </span>

                                <strong>
                                    {formatPrice(
                                        baseTariff.price
                                    )}
                                </strong>
                            </div>

                            <div className="tariff-constructor__summary-row">
                                <span>
                                    {t(
                                        "main.landing.tariffs.constructor.addons"
                                    )}

                                    {selectedAddonIds.length > 0 &&
                                        ` · ${selectedAddonIds.length}`}
                                </span>

                                <strong>
                                    {formatPrice(
                                        tariffSelection
                                            .selectedAddonsTotal
                                    )}
                                </strong>
                            </div>

                            <div className="tariff-constructor__summary-total">
                                <span>
                                    {t(
                                        "main.landing.tariffs.constructor.totalPerMonth"
                                    )}
                                </span>

                                <div>
                                    <strong>
                                        {formatPrice(
                                            tariffSelection
                                                .totalPrice
                                        )}
                                    </strong>

                                    <small>
                                        {t(
                                            "main.landing.tariffs.common.excludingVat"
                                        )}
                                    </small>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="tariff-constructor__grid">
                        {tariffAddons.map(
                            (addon) => {
                                const selected =
                                    selectedAddonIds.includes(
                                        addon.id
                                    );

                                return (
                                    <button
                                        className={`tariff-addon${
                                            selected
                                                ? " tariff-addon--selected"
                                                : ""
                                        }`}
                                        type="button"
                                        aria-pressed={
                                            selected
                                        }
                                        onClick={() =>
                                            toggleAddon(
                                                addon.id
                                            )
                                        }
                                        key={
                                            addon.id
                                        }
                                    >
                                        <span className="tariff-addon__checkbox">
                                            {selected && (
                                                <LandingCheckIcon/>
                                            )}
                                        </span>

                                        <span className="tariff-addon__content">
                                            <strong>
                                                {t(
                                                    addon.nameKey
                                                )}
                                            </strong>

                                            <span>
                                                {t(
                                                    addon.descriptionKey
                                                )}
                                            </span>
                                        </span>

                                        <span className="tariff-addon__price">
                                            <strong>
                                                +{" "}
                                                {formatPrice(
                                                    addon.price
                                                )}
                                            </strong>

                                            <span>
                                                {t(
                                                    "main.landing.tariffs.common.perMonth"
                                                )}
                                            </span>
                                        </span>
                                    </button>
                                );
                            }
                        )}
                    </div>

                    <div className="tariff-constructor__footer">
                        <div className="tariff-constructor__footer-text">
                            {t(
                                "main.landing.tariffs.constructor.selectedAddons"
                            )}
                            :{" "}
                            <strong>
                                {
                                    selectedAddonIds.length
                                }
                            </strong>
                        </div>

                        <a
                            href="/register"
                            className="tariff-constructor__button"
                        >
                            {t(
                                "main.landing.tariffs.constructor.startWithPlan"
                            )}

                            <LandingArrowIcon/>
                        </a>
                    </div>
                </div>
            )}
        </>
    );
}
